import {
  API_PREFIX,
  HttpError,
  paginatedFromDto,
  type HttpClient,
} from "@nasam/kernel";
import type {
  AssetCreateInput,
  AssetsRepository,
  AssetUpdateInput,
} from "../domain";
import {
  archivedAssetsListResponseDtoSchema,
  assetArchivedMatchResponseDtoSchema,
  assetCreateDtoSchema,
  assetCreateFormResponseDtoSchema,
  assetDeleteResponseDtoSchema,
  assetDetailResponseDtoSchema,
  assetResponseDtoSchema,
  assetsListResponseDtoSchema,
  assetUpdateDtoSchema,
  type AssetCreateDto,
  type AssetUpdateDto,
} from "./assetDtos";
import {
  archivedAssetFromDto,
  assetArchivedMatchFromDto,
  assetCreateFormFromDto,
  assetCreateToDto,
  assetDetailFromDto,
  assetFromDto,
  assetListItemFromDto,
  assetUpdateToDto,
} from "./assetMappers";

const ASSETS_PATH = `${API_PREFIX}/assets`;

// Only used once at least one file is being uploaded -- `attributes` has to
// travel as a JSON string in multipart form fields (see the POST /assets
// swagger note); everything else stays a plain string field. Mirrors
// companyMultipartBody's shape.
function assetCreateMultipartBody(
  dto: AssetCreateDto,
  input: AssetCreateInput,
): FormData {
  const body = new FormData();
  if (dto.accountId !== undefined) body.set("accountId", dto.accountId);
  body.set("assetTypeId", dto.assetTypeId);
  body.set("branchId", dto.branchId);
  body.set("nameEn", dto.nameEn);
  body.set("nameAr", dto.nameAr);
  if (dto.notes !== undefined) body.set("notes", dto.notes);
  body.set("attributes", JSON.stringify(dto.attributes));
  for (const attachment of input.attachments ?? []) {
    if (attachment.file !== undefined) {
      body.append("attachments", attachment.file, attachment.fileName);
    }
  }
  return body;
}

// Assumed to mirror companyMultipartBody's add/remove pair -- see the note
// on AssetUpdateInput.
function assetUpdateMultipartBody(
  dto: AssetUpdateDto,
  input: AssetUpdateInput,
): FormData {
  const body = new FormData();
  if (dto.status !== undefined) body.set("status", dto.status);
  if (dto.nameEn !== undefined) body.set("nameEn", dto.nameEn);
  if (dto.nameAr !== undefined) body.set("nameAr", dto.nameAr);
  if (dto.notes !== undefined) body.set("notes", dto.notes);
  if (dto.branchId !== undefined) body.set("branchId", dto.branchId);
  if (dto.attributes !== undefined) {
    body.set("attributes", JSON.stringify(dto.attributes));
  }
  for (const attachment of input.attachments ?? []) {
    if (attachment.file !== undefined) {
      body.append("attachments", attachment.file, attachment.fileName);
    }
  }
  for (const id of input.attachmentsIdsToBeDeleted ?? []) {
    body.append("attachmentsIdsToBeDeleted[]", id);
  }
  return body;
}

export function createAssetsHttpRepository(http: HttpClient): AssetsRepository {
  return {
    async list(query) {
      const response = await http.get(ASSETS_PATH, { params: query });
      return paginatedFromDto(
        assetsListResponseDtoSchema.parse(response),
        assetListItemFromDto,
      );
    },
    async create(input) {
      const dto = assetCreateDtoSchema.parse(assetCreateToDto(input));
      const hasFiles = (input.attachments ?? []).length > 0;
      try {
        const response = await http.post(
          ASSETS_PATH,
          hasFiles ? assetCreateMultipartBody(dto, input) : dto,
        );
        const created = assetResponseDtoSchema.parse(response);
        const asset = assetFromDto(created.data);
        return {
          status: "created",
          asset,
          ...(created.message !== undefined
            ? { responseMessage: created.message }
            : {}),
        };
      } catch (error) {
        // Used to answer 201 with only the archived asset's trimmed summary
        // (isArchived: true) when the name matched one; now 409s instead,
        // same trimmed shape under `data` -- still not an error from the
        // form's own perspective, just an offer to restore. Disambiguated
        // by shape, not the code string, same as every other archived-match
        // endpoint in this app.
        const archived =
          error instanceof HttpError && error.status === 409
            ? assetArchivedMatchResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (archived === undefined || !archived.success) throw error;
        return {
          status: "archived-match",
          match: assetArchivedMatchFromDto(archived.data.data),
          message: archived.data.message,
        };
      }
    },
    async getCreateForm(assetTypeId) {
      const response = await http.get(`${ASSETS_PATH}/form`, {
        params: { assetTypeId },
      });
      return assetCreateFormFromDto(
        assetCreateFormResponseDtoSchema.parse(response).data,
      );
    },
    async getById(id) {
      const response = await http.get(`${ASSETS_PATH}/${id}`);
      return assetDetailFromDto(
        assetDetailResponseDtoSchema.parse(response).data,
      );
    },
    async update(id, input) {
      const dto = assetUpdateDtoSchema.parse(assetUpdateToDto(input));
      const hasFiles = (input.attachments ?? []).length > 0;
      const hasDeletions = (input.attachmentsIdsToBeDeleted ?? []).length > 0;
      const response = await http.patch(
        `${ASSETS_PATH}/${id}`,
        hasFiles || hasDeletions ? assetUpdateMultipartBody(dto, input) : dto,
      );
      const parsed = assetResponseDtoSchema.parse(response);
      const asset = assetFromDto(parsed.data);
      return {
        ...asset,
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },
    async delete(id) {
      const response = await http.delete(`${ASSETS_PATH}/${id}`);
      const { message } = assetDeleteResponseDtoSchema.parse(response);
      return { responseMessage: message };
    },
    async restore(id) {
      const response = await http.patch(`${ASSETS_PATH}/${id}/restore`);
      const parsed = assetResponseDtoSchema.parse(response);
      const asset = assetFromDto(parsed.data);
      return {
        ...asset,
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },
    async listArchived(query) {
      const response = await http.get(`${ASSETS_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedAssetsListResponseDtoSchema.parse(response),
        archivedAssetFromDto,
      );
    },
  };
}
